import { LocalStore, type OutboxEntry } from './idb.ts';
import type { ChangeEvent } from './types.ts';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string | null;
  readonly details: unknown;

  constructor(status: number, message: string, code: string | null = null, details: unknown = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }

  /** `true` si l'erreur vient d'une coupure réseau plutôt que du serveur. */
  get isOffline(): boolean {
    return this.status === 0;
  }
}

/** Aligné sur MAX_UPLOAD_BYTES de l'API : au-delà, l'envoi passe directement par le stockage. */
export const DIRECT_UPLOAD_THRESHOLD_BYTES = 25 * 1024 * 1024;

export type ConnectionState = 'online' | 'offline' | 'syncing';

export interface ClientOptions {
  baseUrl: string;
  /** Nom de la base IndexedDB — distinct pour l'app Staff et l'app Parents. */
  storeName: string;
  /**
   * Application appelante. Le serveur s'en sert pour choisir le bon cookie :
   * en production les deux domaines suffiraient, mais en développement les deux
   * applications partagent `localhost`, et les cookies ignorent le port.
   */
  app: 'staff' | 'parents';
  /**
   * Ancienne clé du jeton dans le `localStorage`.
   *
   * À retirer après le 15 septembre 2026 avec le reste de la passerelle : elle
   * ne sert plus qu'à récupérer une session ouverte avant le passage au cookie.
   */
  legacyTokenKey: string;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  /** Mettre la réponse en cache et la servir hors connexion (GET uniquement). */
  cache?: boolean;
  /** Mettre la mutation en file d'attente si le réseau est absent. */
  queueOffline?: boolean;
  /** Libellé lisible de la mutation, affiché dans l'indicateur de synchronisation. */
  label?: string;
  signal?: AbortSignal;
}

type Listener<T> = (payload: T) => void;

/**
 * Client HTTP + WebSocket de Bala'App.
 *
 * Trois responsabilités :
 *  - appels REST authentifiés ;
 *  - cache local des GET, pour un démarrage instantané et le mode hors connexion ;
 *  - file d'attente des mutations effectuées hors connexion, rejouée au retour du réseau.
 *
 * Le jeton de session n'est jamais visible d'ici : il vit dans un cookie
 * `HttpOnly` posé par le serveur, que le navigateur joint tout seul aux
 * requêtes de même origine. C'est ce qui met la session hors de portée d'un
 * script injecté dans la page — la lecture d'un `localStorage`, elle, n'aurait
 * demandé qu'une ligne.
 */
export class BalaClient {
  readonly store: LocalStore;
  /**
   * Une session est-elle ouverte ? Le client ne peut plus le déduire de la
   * présence d'un jeton : c'est l'application qui le lui dit, après un
   * `/auth/me` réussi.
   */
  private session = false;
  private socket: WebSocket | null = null;
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private flushing = false;
  private changeListeners = new Set<Listener<ChangeEvent>>();
  private stateListeners = new Set<Listener<ConnectionState>>();
  private outboxListeners = new Set<Listener<OutboxEntry[]>>();
  private state: ConnectionState = 'online';

  private readonly options: ClientOptions;

  constructor(options: ClientOptions) {
    this.options = options;
    this.store = new LocalStore(options.storeName);
    this.purgerJetonCsrfHerite();

    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => {
        this.setState('online');
        void this.flushOutbox();
        this.connectSocket();
      });
      window.addEventListener('offline', () => this.setState('offline'));
      if (!navigator.onLine) this.state = 'offline';
    }
  }

  get baseUrl(): string {
    return this.options.baseUrl;
  }

  // -- Session --------------------------------------------------------------

  hasSession(): boolean {
    return this.session;
  }

  /**
   * Déclare l'état de la session. Appelé par l'application après un `/auth/me`
   * réussi, et à l'inverse dès qu'elle sait la session close.
   */
  setSession(active: boolean): void {
    this.session = active;
    if (active) this.connectSocket();
    else this.disconnectSocket();
  }

  /**
   * Récupère une session ouverte avant le passage au cookie.
   *
   * À retirer après le 15 septembre 2026 — la durée de vie d'une session. Sans
   * cette passerelle, le déploiement déconnecterait tout le monde d'un coup :
   * pour les parents, qui n'ouvrent l'application que quelques fois par an, ce
   * serait surtout une vague d'oublis de mot de passe.
   *
   * La clé est effacée quoi qu'il arrive : un échec ramène simplement à l'écran
   * de connexion, ce qui est le comportement attendu d'une session périmée.
   */
  async adoptLegacyToken(): Promise<boolean> {
    if (typeof localStorage === 'undefined') return false;
    const token = localStorage.getItem(this.options.legacyTokenKey);
    if (!token) return false;
    localStorage.removeItem(this.options.legacyTokenKey);

    try {
      const response = await fetch(`${this.options.baseUrl}/auth/session/adopt`, {
        method: 'POST',
        credentials: 'include',
        headers: { Accept: 'application/json', Authorization: `Bearer ${token}`, ...this.appHeader() },
      });
      return response.ok;
    } catch {
      return false;
    }
  }

  /** Efface l'état de session et tout le cache local (déconnexion). */
  async signOut(): Promise<void> {
    this.setSession(false);
    await this.store.clearAll();
  }

  // -- État de connexion ----------------------------------------------------

  getState(): ConnectionState {
    return this.state;
  }

  private setState(next: ConnectionState): void {
    if (this.state === next) return;
    this.state = next;
    for (const listener of this.stateListeners) listener(next);
  }

  onStateChange(listener: Listener<ConnectionState>): () => void {
    this.stateListeners.add(listener);
    return () => this.stateListeners.delete(listener);
  }

  onChange(listener: Listener<ChangeEvent>): () => void {
    this.changeListeners.add(listener);
    return () => this.changeListeners.delete(listener);
  }

  /**
   * Signale un changement aux écrans ouverts dans cet onglet, sans attendre le
   * WebSocket. Après une mutation locale (ex. capital de départ), les hooks
   * `useQuery` qui écoutent l'entité se rechargent tout de suite.
   */
  notifyChange(change: Omit<ChangeEvent, 'at'>): void {
    const event: ChangeEvent = { ...change, at: new Date().toISOString() };
    for (const listener of this.changeListeners) listener(event);
  }

  /** Invalide les GET mis en cache dont la clé commence par l'un des préfixes. */
  invalidateCache(prefixes: string[]): Promise<void> {
    return this.store.invalidate(prefixes);
  }

  onOutboxChange(listener: Listener<OutboxEntry[]>): () => void {
    this.outboxListeners.add(listener);
    return () => this.outboxListeners.delete(listener);
  }

  private async notifyOutbox(): Promise<void> {
    if (this.outboxListeners.size === 0) return;
    const entries = await this.store.listOutbox();
    for (const listener of this.outboxListeners) listener(entries);
  }

  // -- Requêtes -------------------------------------------------------------

  async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const method = options.method ?? 'GET';
    const cacheKey = `${method}:${path}`;
    const headers: Record<string, string> = { Accept: 'application/json', ...this.appHeader() };
    if (options.body !== undefined) headers['Content-Type'] = 'application/json';
    // Le jeton anti-CSRF est relu à chaque appel, jamais mémorisé : la file
    // hors connexion peut rejouer une mutation des heures après l'avoir mise de
    // côté, et une session renouvelée entre-temps aurait changé la valeur.
    if (method !== 'GET') {
      const csrf = this.readCsrfToken();
      if (csrf) headers['X-CSRF-Token'] = csrf;
    }

    try {
      const response = await fetch(`${this.options.baseUrl}${path}`, {
        method,
        headers,
        credentials: 'include',
        body: options.body === undefined ? undefined : JSON.stringify(options.body),
        signal: options.signal,
      });

      if (this.state === 'offline') this.setState('online');

      if (!response.ok) throw await toApiError(response);

      const payload = response.status === 204 ? (undefined as T) : ((await response.json()) as T);
      if (method === 'GET' && options.cache !== false) {
        await this.store.setCache(cacheKey, payload);
      }
      return payload;
    } catch (error) {
      if (error instanceof ApiError) throw error;
      if (isAbort(error)) throw error;

      // Échec réseau : servir le cache en lecture, mettre en file en écriture.
      this.setState('offline');

      if (method === 'GET' && options.cache !== false) {
        const cached = await this.store.getCache<T>(cacheKey);
        if (cached) return cached.value;
      }

      if (method !== 'GET' && options.queueOffline) {
        await this.store.enqueue({
          method,
          path,
          body: options.body ?? null,
          clientId: extractClientId(options.body),
          label: options.label ?? `${method} ${path}`,
        });
        await this.notifyOutbox();
        throw new ApiError(
          0,
          'Hors connexion : la modification est enregistrée et sera envoyée automatiquement.',
          'queued',
        );
      }

      throw new ApiError(0, 'Hors connexion. Réessayez dès que le réseau revient.', 'offline');
    }
  }

  get<T>(path: string, options: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: 'GET' });
  }

  post<T>(path: string, body?: unknown, options: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<T> {
    return this.request<T>(path, { queueOffline: true, ...options, method: 'POST', body });
  }

  patch<T>(path: string, body?: unknown, options: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<T> {
    return this.request<T>(path, { queueOffline: true, ...options, method: 'PATCH', body });
  }

  /**
   * `PUT` sert aux ressources dont la clé est fonctionnelle plutôt que
   * technique — une présence (activité, personne), un budget (catégorie,
   * période). L'opération est idempotente, donc rejouable sans risque depuis la
   * file d'attente hors connexion.
   */
  put<T>(path: string, body?: unknown, options: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<T> {
    return this.request<T>(path, { queueOffline: true, ...options, method: 'PUT', body });
  }

  del<T>(path: string, options: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<T> {
    return this.request<T>(path, { queueOffline: true, ...options, method: 'DELETE' });
  }

  /**
   * Envoi de fichiers. Non mis en file d'attente : nécessite le réseau.
   *
   * Au-delà de DIRECT_UPLOAD_THRESHOLD_BYTES, un fichier part directement vers
   * le stockage via une URL signée par l'API ; les autres passent par le
   * multipart habituel. La réponse garde l'ordre des fichiers fournis.
   */
  async upload<T>(path: string, files: File[], fields: Record<string, string> = {}): Promise<T> {
    const large = files.filter((file) => file.size > DIRECT_UPLOAD_THRESHOLD_BYTES);
    if (large.length === 0) return this.uploadMultipart<T>(path, files, fields);

    const small = files.filter((file) => file.size <= DIRECT_UPLOAD_THRESHOLD_BYTES);
    const multipart = small.length > 0
      ? await this.uploadMultipart<{ files: unknown[]; fields?: Record<string, string>; context?: string | null }>(
          path,
          small,
          fields,
        )
      : null;

    const direct = new Map<File, unknown>();
    for (const file of large) direct.set(file, await this.uploadDirect(file));

    let smallIndex = 0;
    const ordered = files.map((file) =>
      direct.has(file) ? direct.get(file) : multipart?.files[smallIndex++],
    );
    return {
      ...(multipart ?? { fields, context: null }),
      files: ordered,
    } as T;
  }

  private async uploadDirect(file: File): Promise<unknown> {
    const intent = await this.request<{
      uploadId: string;
      uploadUrl: string;
      method: 'PUT';
      headers: Record<string, string>;
    }>('/files/direct-uploads', {
      method: 'POST',
      body: { filename: file.name, mimeType: file.type, sizeBytes: file.size },
    });

    let response: Response;
    try {
      // Pas de cookie ni d'en-tête CSRF : l'URL signée suffit, et le stockage
      // est une autre origine.
      response = await fetch(intent.uploadUrl, { method: intent.method, headers: intent.headers, body: file });
    } catch {
      throw new ApiError(0, `Envoi de « ${file.name} » interrompu. Vérifiez la connexion et réessayez.`, 'offline');
    }
    if (!response.ok) {
      throw new ApiError(response.status, `Le stockage a refusé « ${file.name} » (${response.status}).`, 'storage_rejected');
    }

    const completed = await this.request<{ file: unknown }>(
      `/files/direct-uploads/${encodeURIComponent(intent.uploadId)}/complete`,
      { method: 'POST', body: {} },
    );
    return completed.file;
  }

  private async uploadMultipart<T>(path: string, files: File[], fields: Record<string, string>): Promise<T> {
    const form = new FormData();
    for (const file of files) form.append('files', file);
    for (const [key, value] of Object.entries(fields)) form.append(key, value);

    const headers: Record<string, string> = { ...this.appHeader() };
    const csrf = this.readCsrfToken();
    if (csrf) headers['X-CSRF-Token'] = csrf;

    const response = await fetch(`${this.options.baseUrl}${path}`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: form,
    });
    if (!response.ok) throw await toApiError(response);
    return (await response.json()) as T;
  }

  /**
   * URL de téléchargement d'un fichier.
   *
   * Le jeton y figurait autrefois en clair : une balise `<img>` ou
   * `<a download>` ne peut porter aucun en-tête. Il finissait donc dans les
   * journaux d'accès et dans l'historique du navigateur. Le cookie de session,
   * lui, accompagne la requête sans rien laisser dans l'adresse.
   */
  fileUrl(fileId: string, variant: 'original' | 'thumb' = 'original'): string {
    // `s=1600` distingue l'URL d'une vignette de celle qu'on servait avant
    // (l'original, parfois trop grand pour Safari). Cette ancienne réponse
    // était en cache `immutable` : sans un paramètre nouveau, l'iPhone
    // réafficherait le fichier qu'il n'arrive pas à peindre.
    const query = variant === 'thumb' ? '?variant=thumb&s=1600' : '';
    return `${this.options.baseUrl}/files/${fileId}${query}`;
  }

  // -- File d'attente hors connexion ---------------------------------------

  /**
   * Rejoue les mutations en attente, dans l'ordre. Une entrée rejetée par le
   * serveur (4xx) est abandonnée avec son erreur : la rejouer indéfiniment
   * bloquerait toute la file. Une panne réseau interrompt la boucle et laisse
   * la file intacte pour la prochaine tentative.
   */
  async flushOutbox(): Promise<{ sent: number; failed: number }> {
    if (this.flushing) return { sent: 0, failed: 0 };
    this.flushing = true;
    let sent = 0;
    let failed = 0;

    try {
      const entries = await this.store.listOutbox();
      if (entries.length === 0) return { sent: 0, failed: 0 };
      this.setState('syncing');

      for (const entry of entries) {
        if (entry.id == null) continue;
        try {
          await this.request(entry.path, {
            method: entry.method,
            body: entry.body ?? undefined,
            queueOffline: false,
          });
          await this.store.dequeue(entry.id);
          sent += 1;
        } catch (error) {
          if (error instanceof ApiError && error.isOffline) {
            // Toujours hors connexion : on garde la file et on s'arrête là.
            this.setState('offline');
            break;
          }
          const message = error instanceof Error ? error.message : String(error);
          await this.store.updateOutbox({ ...entry, attempts: entry.attempts + 1, lastError: message });
          if (entry.attempts + 1 >= 3) await this.store.dequeue(entry.id);
          failed += 1;
        }
      }

      await this.notifyOutbox();
      if (this.state === 'syncing') this.setState('online');
      return { sent, failed };
    } finally {
      this.flushing = false;
    }
  }

  pendingCount(): Promise<number> {
    return this.store.listOutbox().then((entries) => entries.length);
  }

  // -- Synchronisation temps réel ------------------------------------------

  /**
   * Ouvre le canal WebSocket de diffusion des changements. Reconnexion
   * automatique avec backoff exponentiel plafonné à 30 s.
   */
  connectSocket(): void {
    if (typeof WebSocket === 'undefined' || !this.session) return;
    if (this.socket && (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)) {
      return;
    }
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    // `baseUrl` est relatif (`/api`) : l'adresse absolue se construit depuis
    // l'origine de la page, et le cookie de session part avec la poignée de
    // main comme pour n'importe quelle requête de même origine.
    const wsUrl = absoluteWsUrl(this.options.baseUrl) + '/sync';
    let socket: WebSocket;
    try {
      socket = new WebSocket(wsUrl);
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.socket = socket;

    socket.onopen = () => {
      this.reconnectAttempts = 0;
      this.setState('online');
      void this.flushOutbox();
    };

    socket.onmessage = (event) => {
      try {
        const parsed = JSON.parse(String(event.data)) as { type: string; change?: ChangeEvent };
        if (parsed.type === 'change' && parsed.change) {
          for (const listener of this.changeListeners) listener(parsed.change);
        }
      } catch {
        /* message non exploitable : ignoré */
      }
    };

    socket.onclose = () => {
      this.socket = null;
      this.scheduleReconnect();
    };

    socket.onerror = () => socket.close();
  }

  private scheduleReconnect(): void {
    if (!this.session || this.reconnectTimer) return;
    const delay = Math.min(30_000, 1_000 * 2 ** this.reconnectAttempts);
    this.reconnectAttempts += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connectSocket();
    }, delay);
  }

  disconnectSocket(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.socket?.close();
    this.socket = null;
  }

  // -- Interne --------------------------------------------------------------

  private appHeader(): Record<string, string> {
    return { 'X-Bala-App': this.options.app };
  }

  /**
   * Le jeton anti-CSRF, posé par le serveur dans un cookie volontairement
   * lisible. Le renvoyer dans un en-tête prouve que l'appel vient d'une page de
   * même origine : un formulaire hébergé ailleurs enverrait bien le cookie de
   * session, mais ne pourrait pas poser cet en-tête.
   */
  /**
   * Efface le jeton anti-CSRF laissé par un déploiement à origine unique.
   *
   * Quand les pages et l'API partageaient la même origine, ce cookie était posé
   * sur le domaine des pages. En passant à une API sur un sous-domaine voisin,
   * le serveur le pose désormais sur le domaine racine — et l'ancien, de même
   * nom et de même chemin, **reste à côté du nouveau**. `document.cookie` rend
   * alors deux valeurs sans dire laquelle vient d'où, et lire la mauvaise fait
   * refuser toutes les écritures par une erreur que rien n'explique.
   *
   * Un cookie s'efface avec la portée qui l'a créé : sans attribut `Domain`,
   * cette suppression ne touche que la variante attachée à l'hôte — l'ancienne
   * — et laisse intacte celle du domaine racine.
   *
   * Ne s'exécute que si l'API est sur une autre origine : dans un déploiement à
   * origine unique, le cookie attaché à l'hôte est le bon, et l'effacer
   * casserait ce qui marche.
   */
  private purgerJetonCsrfHerite(): void {
    if (typeof document === 'undefined') return;
    if (!/^https?:/.test(this.options.baseUrl)) return;
    this.effacerJetonCsrfDHote();
  }

  /**
   * Efface la seule variante attachée à l'hôte : sans attribut `Domain`, une
   * suppression ne peut pas atteindre celle du domaine racine.
   */
  private effacerJetonCsrfDHote(): void {
    const secure = typeof location !== 'undefined' && location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `bala_${this.options.app}_csrf=; Max-Age=0; Path=/; SameSite=Lax${secure}`;
  }

  private readCsrfToken(): string | null {
    if (typeof document === 'undefined') return null;
    const name = `bala_${this.options.app}_csrf=`;
    const valeurs = (source: string) =>
      source.split(';').map((part) => part.trim()).filter((part) => part.startsWith(name));

    let trouves = valeurs(document.cookie);

    // Deux cookies de même nom : c'est la bascule vers une API sur un
    // sous-domaine voisin qui les a créés. Le serveur pose désormais le jeton
    // sur le domaine racine, et celui d'avant — attaché à l'hôte — reste à
    // côté sans que rien ne le distingue ici : `document.cookie` ne dit pas
    // d'où vient une valeur, et l'ancienne est rendue en premier.
    //
    // Lire la mauvaise fait refuser toutes les écritures par un 403 que rien
    // n'explique. On efface donc la variante d'hôte dès qu'on constate le
    // doublon — le moment où le doublon apparaît étant une connexion, pas le
    // chargement de la page, ce rattrapage doit vivre ici et pas seulement au
    // démarrage.
    if (trouves.length > 1) {
      this.effacerJetonCsrfDHote();
      trouves = valeurs(document.cookie);
    }

    const premier = trouves[0];
    return premier === undefined ? null : decodeURIComponent(premier.slice(name.length));
  }
}

// ---------------------------------------------------------------------------

/**
 * Adresse absolue du canal temps réel. `baseUrl` vaut `/api` : sans origine,
 * `new WebSocket()` échouerait.
 */
function absoluteWsUrl(baseUrl: string): string {
  if (/^https?:/.test(baseUrl)) return baseUrl.replace(/^http/, 'ws');
  const origin = typeof location === 'undefined' ? '' : location.origin;
  return origin.replace(/^http/, 'ws') + baseUrl;
}

async function toApiError(response: Response): Promise<ApiError> {
  let message = `Erreur ${response.status}`;
  let code: string | null = null;
  let details: unknown = null;
  try {
    const payload = (await response.json()) as { error?: string; code?: string; details?: unknown };
    if (payload.error) message = payload.error;
    code = payload.code ?? null;
    details = payload.details ?? null;
  } catch {
    /* réponse non JSON : on garde le message par défaut */
  }
  return new ApiError(response.status, message, code, details);
}

function isAbort(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

function extractClientId(body: unknown): string | null {
  if (body && typeof body === 'object' && 'id' in body) {
    const id = (body as { id?: unknown }).id;
    if (typeof id === 'string') return id;
  }
  return null;
}

/**
 * Identifiant unique généré côté client. Permet de créer une entité hors
 * connexion et de conserver le même identifiant après synchronisation.
 */
export function newId(prefix = ''): string {
  const random =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID().replace(/-/g, '').slice(0, 20)
      : Math.random().toString(36).slice(2) + Date.now().toString(36);
  return prefix ? `${prefix}_${random}` : random;
}
